import React, { createContext, useContext, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import CookieUtils from "../utils/cookie-utils";

// Define the type for the context value
interface SocketContextValue {
  socket: Socket | null;
  connected: boolean;
  initializeSocket: (token: string) => Promise<void>;
  disconnectSocket: () => void;
}

// Create the context with the defined type
const SocketContext = createContext<SocketContextValue | undefined>(undefined);

export const useSocket = (): SocketContextValue => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const initializeSocket = async (token: string) => {
    try {
      const socket = io(`${CookieUtils.getDomain().realtime_domain}/employees`, {
        // transports: ["polling"],
        extraHeaders: {
          Authorization: `${token}`,
          Accept: "application/json",
        },
        reconnectionAttempts: 10, // number of reconnection attempts before giving up
        reconnectionDelay: 1000, // delay between reconnection attempts
      });
      socket.on("connect", () => {
        console.log("Connected to socket server");
        setConnected(true);
      });

      socket.on("disconnect", () => {
        console.log("Disconnected from socket server");
        setConnected(false);
      });

      socket.on("connect_error", (error) => {
        console.error("Connection error:", error);
      });

      socketRef.current = socket;
    } catch (error) {
      console.error("Error initializing socket:", error);
    }
  };
  const disconnectSocket = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setConnected(false);
      console.log("Socket disconnected");
    }
  };

  return (
    <SocketContext.Provider value={{ socket: socketRef.current, connected, initializeSocket, disconnectSocket }}>
      {children}
    </SocketContext.Provider>
  );
};
